import { Avatar } from "@chakra-ui/avatar";
import { Tooltip } from "@chakra-ui/tooltip";
import {
  Box,
  Text,
  Input,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import ScrollableFeed from "react-scrollable-feed";
import {
  isLastMessage,
  isSameSender,
  isSameUser,
  decodeStoredContent,
  formatMessageTime,
  formatDateDivider,
  isDifferentDay,
  groupReactions,
  getMessageSenderId,
} from "../config/ChatLogics";
import { ChatState } from "../Context/ChatProvider";
import "../components/ChatsDark.css";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const ScrollableChat = ({
  messages,
  isGroupChat,
  isGroupAdmin,
  editingId,
  editDraft,
  setEditDraft,
  onSaveEdit,
  onCancelEdit,
  onEditStart,
  onDelete,
  onReact,
  onReply,
  onForward,
}) => {
  const { user } = ChatState();

  const canDelete = (m) => {
    const senderId = getMessageSenderId(m);
    if (!senderId) return false;
    if (senderId === String(user?._id)) return true;
    return !!(isGroupChat && isGroupAdmin);
  };

  return (
    <ScrollableFeed className="thread-scroll">
      {messages &&
        messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const showDivider =
            !prev || isDifferentDay(prev.createdAt, m.createdAt);
          const isMine = getMessageSenderId(m) === String(user?._id);
          const showAvatar =
            isSameSender(messages, m, i, user?._id) ||
            isLastMessage(messages, i, user?._id);
          const showSenderName =
            !!isGroupChat &&
            !isMine &&
            (!prev ||
              getMessageSenderId(prev) !== getMessageSenderId(m) ||
              isDifferentDay(prev.createdAt, m.createdAt));
          const reactions = groupReactions(m.reactions, user?._id);
          const isEditing = editingId === String(m._id);
          const deleted = !!m.isDeleted;
          const grouped = isSameUser(messages, m, i, user?._id);
          const showReacts = reactions.length > 0 && !deleted;

          return (
            <div
              key={m._id}
              className={`msg-row ${isMine ? "mine" : "theirs"} ${
                grouped ? "grouped" : ""
              }`}
            >
              {showDivider && (
                <div className="msg-divider">
                  <span className="msg-divider-line" />
                  <span className="msg-divider-pill">
                    {formatDateDivider(m.createdAt)}
                  </span>
                  <span className="msg-divider-line" />
                </div>
              )}
              <div className="msg-line">
                {!isMine && (
                  <div className="msg-avatar-slot">
                    {showAvatar ? (
                      <Tooltip
                        label={m.sender?.name}
                        placement="bottom-start"
                        hasArrow
                      >
                        <Avatar
                          size="xs"
                          cursor="pointer"
                          name={m.sender?.name}
                          src={m.sender?.pic}
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                )}
                <div className="msg-col">
                  {showSenderName && (
                    <Text className="msg-sender" isTruncated>
                      {m.sender?.name || "Unknown"}
                    </Text>
                  )}
                  <div className="msg-hover">
                    <div
                      className={`msg-bubble-wrap ${
                        showReacts ? "has-reacts" : ""
                      }`}
                    >
                    <div
                      className={`msg-bubble ${isMine ? "mine" : "theirs"} ${
                        deleted ? "deleted" : ""
                      }`}
                    >
                      {m.isForwarded && !deleted && (
                        <Text className="msg-fwd">Forwarded</Text>
                      )}
                      {m.replyTo && !deleted && (
                        <Box className="msg-reply">
                          <Text className="msg-reply-name" isTruncated>
                            {m.replyTo?.sender?.name || "Reply"}
                          </Text>
                          <Text className="msg-reply-text" isTruncated>
                            {m.replyTo?.isDeleted
                              ? "This message was deleted"
                              : decodeStoredContent(m.replyTo?.content || "")}
                          </Text>
                        </Box>
                      )}
                      {isEditing ? (
                        <Box display="flex" gap={2} alignItems="center" minW="220px">
                          <Input
                            size="sm"
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") onSaveEdit(m);
                              if (e.key === "Escape") onCancelEdit();
                            }}
                            bg="var(--ch-edit-bg)"
                            autoFocus
                          />
                          <Box display="flex" gap={1}>
                            <Text
                              as="button"
                              fontSize="12px"
                              fontWeight="800"
                              color="var(--ch-accent)"
                              onClick={() => onSaveEdit(m)}
                            >
                              Save
                            </Text>
                            <Text
                              as="button"
                              fontSize="12px"
                              color="var(--ch-dim)"
                              onClick={onCancelEdit}
                            >
                              Cancel
                            </Text>
                          </Box>
                        </Box>
                      ) : (
                        <Text
                          className={`msg-text ${deleted ? "is-deleted" : ""}`}
                        >
                          {deleted
                            ? "This message was deleted"
                            : decodeStoredContent(m.content)}
                        </Text>
                      )}
                    </div>
                      {showReacts && (
                        <div
                          className="msg-react-pill"
                          role="group"
                          aria-label="Reactions"
                        >
                          {reactions.map((r) => (
                            <Text
                              key={r.emoji}
                              as="button"
                              className={`msg-react ${
                                r.reactedByMe ? "mine" : ""
                              }`}
                              onClick={() => onReact(m._id, r.emoji)}
                              title={r.userIds.join(", ")}
                            >
                              {r.emoji}
                              {r.count > 1 ? ` ${r.count}` : ""}
                            </Text>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Flat meta line OUTSIDE the bubble: time • edited • actions */}
                  {!isEditing && (
                    <div className="msg-meta">
                      <span className="msg-time">
                        {formatMessageTime(m.createdAt)}
                      </span>
                      {m.isEdited && !deleted && (
                        <span className="msg-edited">· edited</span>
                      )}
                      {!deleted && (
                        <span className="msg-tools" role="toolbar" aria-label="Message actions">
                          <Menu placement="top-start">
                            <MenuButton
                              as={IconButton}
                              aria-label="React with emoji"
                              icon={<Text fontSize="13px">😊</Text>}
                              className="msg-tool"
                              size="xs"
                              variant="ghost"
                            />
                            <MenuList
                              bg="var(--ch-menu)"
                              borderColor="var(--ch-panel-border)"
                              minW="0"
                              px={2}
                              py={2}
                            >
                              <Box display="flex" gap={1}>
                                {QUICK_EMOJIS.map((e) => (
                                  <Text
                                    key={e}
                                    as="button"
                                    fontSize="19px"
                                    px={1}
                                    borderRadius="8px"
                                    _hover={{ transform: "scale(1.2)" }}
                                    onClick={() => onReact(m._id, e)}
                                  >
                                    {e}
                                  </Text>
                                ))}
                              </Box>
                            </MenuList>
                          </Menu>
                          <IconButton
                            aria-label="Reply to message"
                            icon={<Text fontSize="13px">↩</Text>}
                            className="msg-tool"
                            size="xs"
                            variant="ghost"
                            onClick={() => onReply(m)}
                          />
                          <IconButton
                            aria-label="Forward message"
                            icon={<Text fontSize="13px">➦</Text>}
                            className="msg-tool"
                            size="xs"
                            variant="ghost"
                            onClick={() => onForward(m)}
                          />
                          <Menu placement="top-end">
                            <MenuButton
                              as={IconButton}
                              aria-label="More message actions"
                              icon={<Text fontSize="14px">⋯</Text>}
                              className="msg-tool"
                              size="xs"
                              variant="ghost"
                            />
                            <MenuList
                              bg="var(--ch-menu)"
                              borderColor="var(--ch-panel-border)"
                              minW="160px"
                            >
                              {isMine && (
                                <MenuItem
                                  _hover={{ bg: "var(--ch-item-hover)" }}
                                  _focus={{ bg: "var(--ch-item-hover)" }}
                                  onClick={() => onEditStart(m)}
                                >
                                  Edit
                                </MenuItem>
                              )}
                              {canDelete(m) && (
                                <MenuItem
                                  _hover={{ bg: "rgba(255,0,0,0.15)" }}
                                  _focus={{ bg: "rgba(255,0,0,0.2)" }}
                                  color="var(--ch-danger-text)"
                                  onClick={() => onDelete(m)}
                                >
                                  Delete
                                </MenuItem>
                              )}
                              {!isMine && !canDelete(m) && (
                                <MenuItem isDisabled fontSize="12px">
                                  No more actions
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        </span>
                      )}
                    </div>
                  )}

                  {/* closes msg-hover */}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </ScrollableFeed>
  );
};

export default ScrollableChat;
